import React, { useState } from 'react';
import { X, Settings, Cookie, Shield, BarChart3, Target } from 'lucide-react';
import { useCookieConsent } from '../contexts/CookieConsentContext';

interface CookieConsentBannerProps {
  language: 'de' | 'en';
  onShowPrivacy?: () => void;
}

export const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({ language, onShowPrivacy }) => {
  const { 
    showBanner, 
    settings, 
    acceptAll, 
    acceptNecessary, 
    updateConsent, 
    closeBanner 
  } = useCookieConsent();
  
  const [showDetails, setShowDetails] = useState(false);
  const [tempSettings, setTempSettings] = useState(settings);

  if (!showBanner) return null;

  const content = {
    de: {
      title: 'Cookie-Einstellungen',
      description: 'Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten. Einige sind für die Funktionalität der Website erforderlich, während andere uns helfen, die Website zu verbessern.',
      acceptAll: 'Alle akzeptieren',
      acceptNecessary: 'Nur notwendige',
      settings: 'Einstellungen',
      saveSettings: 'Einstellungen speichern',
      close: 'Schließen',
      necessary: 'Notwendige Cookies',
      functional: 'Funktionale Cookies',
      analytics: 'Analyse Cookies',
      marketing: 'Marketing Cookies',
      necessaryDesc: 'Diese Cookies sind für die grundlegende Funktionalität der Website erforderlich und können nicht deaktiviert werden.',
      functionalDesc: 'Diese Cookies ermöglichen erweiterte Funktionen wie das Kontaktformular und Barrierefreiheitseinstellungen.',
      analyticsDesc: 'Diese Cookies helfen uns zu verstehen, wie Besucher mit der Website interagieren, indem sie anonyme Informationen sammeln.',
      marketingDesc: 'Diese Cookies werden verwendet, um Ihnen relevante Werbung basierend auf Ihren Interessen zu zeigen.',
      privacyPolicy: 'Datenschutzerklärung',
      legalNotice: 'Impressum'
    },
    en: {
      title: 'Cookie Settings',
      description: 'We use cookies to provide you with the best possible experience on our website. Some are required for the website functionality, while others help us improve the website.',
      acceptAll: 'Accept All',
      acceptNecessary: 'Only Necessary',
      settings: 'Settings',
      saveSettings: 'Save Settings',
      close: 'Close',
      necessary: 'Necessary Cookies',
      functional: 'Functional Cookies',
      analytics: 'Analytics Cookies',
      marketing: 'Marketing Cookies',
      necessaryDesc: 'These cookies are required for the basic functionality of the website and cannot be disabled.',
      functionalDesc: 'These cookies enable enhanced features like the contact form and accessibility settings.',
      analyticsDesc: 'These cookies help us understand how visitors interact with the website by collecting anonymous information.',
      marketingDesc: 'These cookies are used to show you relevant advertising based on your interests.',
      privacyPolicy: 'Privacy Policy',
      legalNotice: 'Legal Notice'
    }
  };

  const currentContent = content[language];

  const handleToggle = (category: keyof typeof tempSettings) => {
    if (category === 'necessary' || category === 'consentVersion') return; // Cannot toggle necessary cookies
    
    setTempSettings(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleSaveSettings = () => {
    updateConsent(tempSettings);
    setShowDetails(false);
  };

  const cookieCategories = [
    {
      key: 'necessary' as const,
      icon: Shield,
      name: currentContent.necessary,
      description: currentContent.necessaryDesc,
      required: true
    },
    {
      key: 'functional' as const,
      icon: Settings,
      name: currentContent.functional,
      description: currentContent.functionalDesc,
      required: false
    },
    {
      key: 'analytics' as const,
      icon: BarChart3,
      name: currentContent.analytics,
      description: currentContent.analyticsDesc,
      required: false
    },
    {
      key: 'marketing' as const,
      icon: Target,
      name: currentContent.marketing,
      description: currentContent.marketingDesc,
      required: false
    }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 pointer-events-none">
      <div className="bg-white border border-gray-200 rounded-lg shadow-2xl max-w-4xl w-full pointer-events-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Cookie className="w-6 h-6 text-brand-beige" />
            <h2 className="text-xl font-bold text-gray-900">
              {currentContent.title}
            </h2>
          </div>
          <button
            onClick={closeBanner}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label={currentContent.close}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-700 mb-6 leading-relaxed">
            {currentContent.description}
          </p>

          {!showDetails ? (
            /* Simple Banner */
            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <button
                onClick={() => setShowDetails(true)}
                className="px-6 py-3 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Settings className="w-4 h-4 inline mr-2" />
                {currentContent.settings}
              </button>
              <button
                onClick={acceptNecessary}
                className="px-6 py-3 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {currentContent.acceptNecessary}
              </button>
              <button
                onClick={acceptAll}
                className="px-6 py-3 bg-brand-beige text-white rounded-lg hover:bg-brand-dark transition-colors"
              >
                {currentContent.acceptAll}
              </button>
            </div>
          ) : (
            /* Detailed Settings */
            <div className="space-y-6">
              {cookieCategories.map(({ key, icon: Icon, name, description, required }) => (
                <div key={key} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <Icon className="w-5 h-5 text-brand-beige" />
                      <h3 className="font-semibold text-gray-900">{name}</h3>
                      {required && (
                        <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                          {language === 'de' ? 'Erforderlich' : 'Required'}
                        </span>
                      )}
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={tempSettings[key] as boolean}
                        onChange={() => handleToggle(key)}
                        disabled={required}
                      />
                      <div className={`w-11 h-6 rounded-full transition-colors ${
                        tempSettings[key] 
                          ? 'bg-brand-beige' 
                          : 'bg-gray-300'
                      } ${required ? 'opacity-50' : ''}`}>
                        <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                          tempSettings[key] ? 'translate-x-5' : 'translate-x-0'
                        }`} />
                      </div>
                    </label>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {description}
                  </p>
                </div>
              ))}

              <div className="flex flex-col sm:flex-row gap-3 justify-between pt-4 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={onShowPrivacy}
                    className="text-sm text-brand-beige hover:text-brand-dark underline text-left"
                  >
                    {currentContent.privacyPolicy}
                  </button>
                  <a
                    href="#impressum"
                    className="text-sm text-brand-beige hover:text-brand-dark underline"
                  >
                    {currentContent.legalNotice}
                  </a>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => setShowDetails(false)}
                    className="px-6 py-3 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {currentContent.close}
                  </button>
                  <button
                    onClick={handleSaveSettings}
                    className="px-6 py-3 bg-brand-beige text-white rounded-lg hover:bg-brand-dark transition-colors"
                  >
                    {currentContent.saveSettings}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
